import { applyCommand } from "../command";
import { hexEquals } from "../hex";
import { workingCities } from "../land/cities";
import { rankBy } from "../pick";
import { findHex } from "../run/board";
import { seatAt, seatUnit, soldierSeats } from "../run/seats";
import { allied } from "../run/sides";
import type { RunState, SeatState, Unit } from "../types/state";
import { isBroken } from "../units/morale";
import { marketFor, marketsOf } from "./market";

/**
 * The soldier seat's script (K1, ADR-0017 point 2: an empty seat is played by a script). It plays
 * every unheld soldier seat through that seat's own door, as a human would, on the city script's
 * cadence and staggered by the seat's number. Its unit is otherwise the King's man: its city's
 * duty deals it a goal, taken as its own word (`sendOnDuty`). The script does what the duty does
 * not: a load in hand is sold at the market at hand, else hauled to the nearest one; a dead seat
 * rises by the first city, in map order, whose arrow points at land not its kingdom's.
 */
export function scriptedSeats(state: RunState): RunState {
  if (!state.rules.soldier?.seats) {
    return state;
  }
  const cadence = state.rules.cityCommand?.scriptCommandTicks ?? state.rules.aiCadenceTicks;
  let next = state;
  for (const seat of soldierSeats(state)) {
    if (!seat.held && state.tick % cadence === seat.id % cadence) {
      next = seatTurn(next, seat.id);
    }
  }
  return next;
}

function seatTurn(state: RunState, id: number): RunState {
  const seat = seatAt(state, id);
  if (!seat || seat.held) {
    return state;
  }
  const unit = seatUnit(state, seat);
  if (!unit) {
    return deployTurn(state, seat);
  }
  return unit.carry && !isBroken(state, unit) ? haulTurn(state, seat, unit) : state;
}

/** A dead seat picks where the war is: the first own working city whose arrow aims off its land. */
function deployTurn(state: RunState, seat: SeatState): RunState {
  const front = workingCities(state, seat.faction).find((city) => {
    const target = city.arrow && findHex(state, city.arrow);
    return target !== undefined && !allied(state.rules, target.owner, seat.faction);
  });
  return front ? applyCommand(state, seat.id, { kind: "deploy", city: front.coord }) : state;
}

/** A load in hand: sold at the market at hand, else carried to the nearest market (ties: the better price). */
function haulTurn(state: RunState, seat: SeatState, unit: Unit): RunState {
  if (marketFor(state, unit)) {
    return applyCommand(state, seat.id, { kind: "sell", unitId: unit.id });
  }
  const [nearest] = rankBy(marketsOf(state, unit), (market) => market.distance);
  if (!nearest || (unit.destination && hexEquals(unit.destination, nearest.city))) {
    return state;
  }
  return applyCommand(state, seat.id, { kind: "order", unitId: unit.id, to: nearest.city });
}
